import { LucideSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { businessDayBounds } from "@/features/appointments/utils/bookingTime";
import { CalendarAgenda } from "@/features/calendar-events/components/CalendarAgenda";
import { useCalendarEvents } from "@/features/calendar-events/hooks/calendarEventQueries";
import { useAppointments } from "@/features/appointments/hooks/appointmentQueries";
import { Button } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { getBusinessDateKey } from "@/shared/utils/formatters";
import { DashboardActions } from "../components/DashboardActions";
import { DashboardAttention } from "../components/DashboardAttention";
import { DashboardBusinessSetupCard } from "../components/DashboardBusinessSetupCard";
import { DashboardFinanceSummary } from "../components/DashboardFinanceSummary";
import { DashboardHeader } from "../components/DashboardHeader";
import { DashboardIdentity } from "../components/DashboardIdentity";
import { DashboardUpcomingMeetingsSummary } from "../components/DashboardUpcomingMeetingsSummary";
import { useDashboardContext } from "../hooks/useDashboardContext";

export function DashboardPage() {
  const { user, businessId, locationId, business, location, now } = useDashboardContext();
  const appointments = useAppointments();
  const navigate = useNavigate();
  const { goToAppointmentDetails, goToAppointmentAdd, goToSearch, goToCalendarEventDetails } = useAppNavigation();
  const agendaTimezone = business.data?.timezone ?? "UTC";
  const agendaDay = getBusinessDateKey(now, agendaTimezone);
  const agendaRange = businessDayBounds(agendaDay, agendaTimezone);
  const events = useCalendarEvents(agendaRange.startAt, agendaRange.endAt);
  const contextError = business.error || location.error;
  const contextReady = business.data && location.data && location.data.businessId === businessId;

  if (!user || business.isLoading || location.isLoading || contextError || !contextReady) {
    return (
      <Page>
        <DashboardHeader />
        <PageContent>
          <h1 className="sr-only">Home</h1>
          {contextError ? <ErrorState message={contextError.message} onRetry={() => { void business.refetch(); void location.refetch(); }} />
            : !user || business.isLoading || location.isLoading ? <RowSkeleton />
            : <EmptyState message="This business location is unavailable." />}
        </PageContent>
      </Page>
    );
  }

  const timeZone = business.data.timezone;
  const today = getBusinessDateKey(now, timeZone);
  const active = (appointments.data || [])
    .filter((appointment) => appointment.businessId === businessId && appointment.locationId === locationId && appointment.startAt && !["cancelled", "declined", "no_show"].includes(appointment.status))
    .sort((a, b) => a.startAt!.getTime() - b.startAt!.getTime());
  const todayAppointments = active.filter((appointment) => getBusinessDateKey(appointment.startAt!, timeZone) === today);
  const next = active.find((appointment) =>
    (appointment.status === "pending" || appointment.status === "confirmed")
    && (appointment.startAt! >= now || (appointment.endAt && appointment.endAt > now)));
  const retry = () => { void appointments.refetch(); };
  const viewToday = () => navigate(`/b/${businessId}/l/${locationId}/appointments?date=${today}`);
  const upcomingSummary = <DashboardUpcomingMeetingsSummary appointment={next} now={now} timeZone={timeZone} currency={business.data.currency} loading={appointments.isLoading} error={appointments.error} onRetry={retry} />;

  return (
    <Page>
      <DashboardHeader />
      <PageContent className="space-y-[26px] pt-3">
        <div>
          <h1 className="sr-only">Home</h1>
          <DashboardIdentity
            businessName={business.data.name}
            locationName={location.data.name}
            onBook={() => goToAppointmentAdd()}
          />
          <Button
            variant="outline"
            className="mt-3 w-full min-w-0 justify-start gap-3 text-base font-normal text-muted-foreground hover:border-ring/50 hover:bg-card hover:text-muted-foreground"
            aria-haspopup="dialog"
            onClick={goToSearch}
          >
            <LucideSearch aria-hidden="true" />
            Find...
          </Button>
        </div>
        {appointments.isSuccess && !next
          ? <DashboardBusinessSetupCard
              userId={user.id}
              businessId={businessId}
              locationId={locationId}
              fallback={upcomingSummary}
            />
          : upcomingSummary}
        <Section
          title="Today's agenda"
          count={appointments.isLoading ? "Loading bookings…" : appointments.error ? "Bookings unavailable" : `${todayAppointments.length} ${todayAppointments.length === 1 ? "booking" : "bookings"}`}
          action={<SectionAction aria-label="View all today's appointments" onClick={viewToday}>View all</SectionAction>}
        >
          <CalendarAgenda appointments={todayAppointments} events={events.data ?? []} dateKey={today} timezone={timeZone}
            showGaps={false} appointmentsLoading={appointments.isLoading} eventsLoading={events.isLoading}
            appointmentsError={appointments.error} eventsError={events.error}
            onRetryAppointments={retry} onRetryEvents={() => void events.refetch()}
            onAppointmentClick={(selected) => goToAppointmentDetails(selected.id, false)}
            onEventClick={(event) => goToCalendarEventDetails(event.id)} />
        </Section>
        <DashboardAttention appointments={appointments.data || []} businessId={businessId} locationId={locationId} timeZone={timeZone} loading={appointments.isLoading} error={appointments.error} onRetry={retry} />
        <DashboardFinanceSummary businessId={businessId} userId={user.id} isOwner={business.data.ownerId === user.id} />
        <DashboardActions />
      </PageContent>
    </Page>
  );
}
